// src/components/Gatoodle/GatoodleTermine.jsx
// Termine-Verwaltung (Abende im El Gato Muerto) für "El Gatoodle" (US-GATO-002).
// Muster: GatoodleGaeste.jsx (US-GATO-001) / Sitzungen.jsx (Modal-Formular).
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  getAllTermine,
  createTermin,
  updateTermin,
  deleteTermin,
  DEFAULT_MAX_PLAETZE,
} from "../../services/gatoodleTermineService";

const emptyForm = () => ({
  datum: "",
  uhrzeit: "",
  programm: "",
  oeffnungVon: "",
  oeffnungBis: "",
  maxPlaetze: DEFAULT_MAX_PLAETZE,
});

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const GatoodleTermine = ({ readOnly = false, search = "" }) => {
  const [termine, setTermine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Suche filtert nach Namen der angemeldeten Gäst:innen, damit man findet,
  // für welchen Termin sich jemand registriert hat.
  const filteredTermine = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return termine;
    return termine.filter((t) =>
      (t.gaeste || []).some((g) =>
        `${g.vorname} ${g.nachname}`.toLowerCase().includes(term)
      )
    );
  }, [termine, search]);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);

  const loadTermine = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setTermine(await getAllTermine());
    } catch (err) {
      setError(err.message || "Die Termine konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTermine();
  }, [loadTermine]);

  const openCreate = () => {
    setForm(emptyForm());
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (termin) => {
    setForm({
      datum: termin.datum,
      uhrzeit: termin.uhrzeit,
      programm: termin.programm,
      oeffnungVon: termin.oeffnungVon,
      oeffnungBis: termin.oeffnungBis,
      maxPlaetze: termin.maxPlaetze,
    });
    setEditing(termin);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      if (editing) {
        await updateTermin(editing.id, form);
      } else {
        await createTermin(form);
      }
      closeForm();
      await loadTermine();
    } catch (err) {
      setError(err.message || "Der Termin konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (termin) => {
    const warnung =
      termin.angemeldetAnzahl > 0
        ? ` ${termin.angemeldetAnzahl} Anmeldung(en) gehen dabei verloren.`
        : "";
    if (
      !window.confirm(`Termin vom ${formatDate(termin.datum)} wirklich löschen?${warnung}`)
    ) {
      return;
    }
    try {
      setError(null);
      await deleteTermin(termin.id);
      await loadTermine();
    } catch (err) {
      setError(err.message || "Der Termin konnte nicht gelöscht werden.");
    }
  };

  if (loading) {
    return <div className="gatoodle__loading">Termine werden geladen...</div>;
  }

  return (
    <div className="gatoodle__section">
      <div className="gatoodle__header">
        <h3>Termine</h3>
        {!readOnly && (
          <button
            type="button"
            className="gatoodle__btn gatoodle__btn--primary"
            onClick={openCreate}
          >
            + Neuer Termin
          </button>
        )}
      </div>

      {error && (
        <div className="gatoodle__error" role="alert" onClick={() => setError(null)}>
          {error}
        </div>
      )}

      <div className="gatoodle__table-wrapper">
        <table className="gatoodle__table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Uhrzeit</th>
              <th>Max. Plätze</th>
              <th>Belegung</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredTermine.length === 0 ? (
              <tr>
                <td className="gatoodle__empty" colSpan={5}>
                  {termine.length === 0
                    ? "Noch keine Termine erfasst."
                    : "Keine Termine gefunden."}
                </td>
              </tr>
            ) : (
              filteredTermine.map((termin) => (
                <tr key={termin.id}>
                  <td>{formatDate(termin.datum)}</td>
                  <td>{termin.uhrzeit}</td>
                  <td>{termin.maxPlaetze}</td>
                  <td>
                    {termin.angemeldetAnzahl}/{termin.maxPlaetze}
                    {termin.gaeste?.length > 0 && (
                      <div className="gatoodle__hint">
                        {termin.gaeste
                          .map((g) => `${g.vorname} ${g.nachname}${g.plus ? " (+1)" : ""}`)
                          .join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="gatoodle__row-actions">
                    {!readOnly && (
                      <>
                        <button
                          type="button"
                          className="gatoodle__btn"
                          onClick={() => openEdit(termin)}
                        >
                          Bearbeiten
                        </button>
                        <button
                          type="button"
                          className="gatoodle__btn gatoodle__btn--danger"
                          onClick={() => handleDelete(termin)}
                        >
                          Löschen
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="gatoodle__modal-overlay" onClick={closeForm}>
          <div className="gatoodle__modal" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSubmit}>
              <div className="gatoodle__modal-header">
                <h3>{editing ? "Termin bearbeiten" : "Neuer Termin"}</h3>
                <button
                  type="button"
                  className="gatoodle__modal-close"
                  onClick={closeForm}
                  aria-label="Schliessen"
                >
                  ×
                </button>
              </div>
              <div className="gatoodle__modal-body">
                <div className="gatoodle__form-grid">
                  <label className="gatoodle__field">
                    <span>Datum</span>
                    <input
                      type="date"
                      value={form.datum}
                      onChange={(e) => handleChange("datum", e.target.value)}
                      required
                    />
                  </label>
                  <label className="gatoodle__field">
                    <span>Uhrzeit</span>
                    <input
                      type="time"
                      value={form.uhrzeit}
                      onChange={(e) => handleChange("uhrzeit", e.target.value)}
                      placeholder="optional"
                    />
                  </label>
                  <label className="gatoodle__field">
                    <span>Max. Plätze</span>
                    <input
                      type="number"
                      min="1"
                      value={form.maxPlaetze}
                      onChange={(e) =>
                        handleChange("maxPlaetze", Number(e.target.value))
                      }
                    />
                  </label>
                  <label className="gatoodle__field">
                    <span>Programm</span>
                    <input
                      type="text"
                      value={form.programm}
                      onChange={(e) => handleChange("programm", e.target.value)}
                      placeholder="optional, auf der externen Seite sichtbar"
                    />
                  </label>
                  <label className="gatoodle__field">
                    <span>Öffnung von</span>
                    <input
                      type="time"
                      value={form.oeffnungVon}
                      onChange={(e) => handleChange("oeffnungVon", e.target.value)}
                      placeholder="optional"
                    />
                  </label>
                  <label className="gatoodle__field">
                    <span>Öffnung bis</span>
                    <input
                      type="time"
                      value={form.oeffnungBis}
                      onChange={(e) => handleChange("oeffnungBis", e.target.value)}
                      placeholder="optional"
                    />
                  </label>
                </div>
              </div>
              <div className="gatoodle__modal-footer">
                <button
                  type="button"
                  className="gatoodle__btn"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="gatoodle__btn gatoodle__btn--primary"
                  disabled={saving}
                >
                  {saving ? "Speichern..." : "Speichern"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GatoodleTermine;
